import './index.css'
import cn from 'clsx'

import React, { useCallback, useContext } from 'react'
import type { PopoverItemType } from 'types/ui'
import { ThemeContext } from 'contexts/theme'
import { PopoverItem } from './PopoverItem'
type Props = {
  compact?: boolean
  items: PopoverItemType[]
  selected?: Pick<PopoverItemType, 'value'> | null | undefined
  short?: boolean
  staticPosition?: boolean
  // DEPRECATED: Use props.items[].onClick instead of props.onClick
  onClick?: (arg0: PopoverItemType) => void
  scrollToTop?: boolean
}
/**
 * A popover component that displays a list of clickable items with support for nested items and themes
 * @param compact - Whether to use compact styling for the popover items
 * @param items - Array of popover items to display
 * @param selected - Currently selected item (compared by value property)
 * @param short - Whether to apply short styling to the popover
 * @param onClick - Callback function called when an item is clicked (deprecated, use item.onClick instead)
 * @param scrollToTop - Whether to scroll to top when items are clicked
 * @param staticPosition - Whether to use static positioning instead of default positioning
 * @returns Popover div element containing the list of interactive items
 */
export const Popover = ({
  compact,
  items,
  selected,
  short,
  onClick,
  scrollToTop,
  staticPosition,
}: Props): React.ReactElement<'div'> => {
  const theme = useContext(ThemeContext).theme
  const handleItemClick = useCallback(
    (item: PopoverItemType) => {
      if (onClick) {
        onClick(item)
      }

      if (item.onClick) {
        item.onClick()
      }
    },
    [onClick],
  )
  const hasNestedItems = items.some(
    item => item?.nested && item.nested.length > 0,
  )
  return (
    <div
      className={cn('popover', {
        'popover--short': short,
        'popover--theme-dark': theme === 'dark',
        'popover--nested': hasNestedItems,
        'popover--static': staticPosition,
      })}
    >
      {items.map((item: PopoverItemType, index: number) => {
        if (!item) return
        return (
          <PopoverItem
            scrollToTop={scrollToTop}
            compact={compact}
            item={item}
            key={item.value || index}
            selected={selected}
            onClick={handleItemClick}
          />
        )
      })}
    </div>
  )
}
